/**
 * useTutorConversation — encapsulates the state/behavior of ONE tutor
 * conversation pane: loading its messages, streaming a turn, feedback,
 * the citation popup, and the budget ("limit reached") banner.
 *
 * Extracted out of TutorOverlay.tsx (which used to own all of this inline)
 * so the same conversation-pane behavior can be reused by both:
 *   - TutorOverlay's "active conversation" column (alongside its own
 *     conversations-list sidebar, which this hook knows nothing about), and
 *   - TutorDrawer, the block-triggered single-conversation side panel, which
 *     has no sidebar at all.
 *
 * This is a BEHAVIOR-PRESERVING extraction for TutorOverlay: every ref/state
 * variable and every branch below is a straight port of what TutorOverlay
 * used to do inline (see git history / TASK-013 for the pre-extraction
 * version) — including the exact suppressNextLoadRef/pendingFocusBlockRef
 * race-avoidance comments, which still apply verbatim.
 *
 * List-management concerns (fetching/selecting/deleting FROM a list of
 * conversations) are NOT here — those stay in TutorOverlay, which pokes at
 * this hook's `selectConversation`/`startNewConversation`/`discardActive`/
 * `setConversationId` to keep the active pane in sync with its own list
 * state. See TutorOverlay.tsx for that wiring.
 */

import type * as React from 'react'
import { useCallback, useEffect, useRef, useState } from 'react'
import {
  createTutorConversation,
  fetchTutorBlockContent,
  fetchTutorConversation,
  setMessageFeedback,
  type TutorBlockContent,
  type TutorConversationSummary,
} from '../tutorApi.js'
import {
  applyTutorEvent,
  EMPTY_TURN_STATE,
  streamTutorMessage,
  type TutorTurnState,
} from '../tutorStream.js'
import type { TutorNavTarget } from '../tutorNav.js'
import type { TutorActionView, TutorCitationView, TutorViewMessage } from './TutorMessageBubble.js'
import type { TutorCitationDialogStatus } from './TutorCitationDialog.js'
import {
  focusBlockPayload,
  toViewMessages,
  viewActionFromEvent,
  type TutorOverlayFocusBlock,
} from './tutorViewModel.js'

export interface UseTutorConversationArgs {
  manualVersionId: number
  /**
   * Conversation to load on mount, or null to start blank (a conversation is
   * created lazily on the first `handleSend`). Read ONCE, at this hook
   * instance's construction (mirrors TutorOverlay's pre-extraction handling
   * of its `focusBlock` prop) — changing it on a later render does NOT make
   * the hook jump to a different conversation; callers that need that
   * (TutorOverlay's sidebar) use `selectConversation`/`setConversationId`
   * instead, which are the actual mutation path this state goes through.
   */
  initialConversationId?: string | null
  /**
   * Pre-fetched messages for `initialConversationId`, already mapped to
   * `TutorViewMessage` (see tutorViewModel.ts's `toViewMessages`) — TASK-014
   * Step B2. When provided (together with a non-null `initialConversationId`),
   * seeds `messages` directly and SKIPS the mount-time GET the load-on-change
   * effect below would otherwise fire, avoiding a redundant round trip when
   * the caller already has the conversation's messages from elsewhere (e.g.
   * Tutor.tsx's Inertia `tutor.active` prop, server-rendered by
   * ReaderController#tutor). Read ONCE, at construction, same as
   * `initialConversationId` itself — irrelevant on every subsequent render.
   */
  initialMessages?: TutorViewMessage[]
  /**
   * Attached to the very next conversation THIS hook instance creates.
   * Consumed once (by the first successful lazy-create in `handleSend`), or
   * dropped if `selectConversation`/`startNewConversation` runs first — in
   * both cases the "chat about this specific block" intent no longer
   * applies to whatever conversation ends up being used.
   */
  focusBlock?: TutorOverlayFocusBlock | null | undefined
  /** Resolves a citation/action target to a navigable slug+anchor. */
  resolveSectionTarget: (sectionId: string, fineId?: string | null) => TutorNavTarget | null
  onNavigateToSection: (slug: string, anchor: string) => void
  onLaunchQuiz: (quizSessionId: string) => void
  /**
   * Called right after `handleSend` lazily creates a new conversation, with
   * the created summary — lets a caller that owns a conversations LIST
   * (TutorOverlay) prepend it. Optional: TutorDrawer has no list to update.
   */
  onConversationCreated?: (conversation: TutorConversationSummary) => void
  /**
   * Called unconditionally at the end of every `handleSend` turn (success,
   * error, or limit-reached alike) — matches the pre-extraction behavior of
   * refetching the conversations list after each turn so its
   * title/last_message_at stay current. Optional: TutorDrawer has no list.
   */
  onTurnSettled?: () => void
}

export interface UseTutorConversationResult {
  conversationId: string | null
  /** Raw setState escape hatch — needed by TutorOverlay's mount-time
   *  "auto-select the most recent conversation" effect, which must use a
   *  FUNCTIONAL update (`current => current ?? list[0].id`) to avoid
   *  clobbering a selection the student already made while that fetch was
   *  in flight. The convenience methods below don't fit that shape. */
  setConversationId: React.Dispatch<React.SetStateAction<string | null>>
  /** User explicitly picked an existing conversation (e.g. sidebar click):
   *  aborts any in-flight stream, resets sending/error, drops any pending
   *  focus block (it no longer applies once an existing conversation is
   *  picked). No-op if already selected. */
  selectConversation: (id: string) => void
  /** User explicitly started a blank conversation (e.g. "+ Nuova
   *  conversazione"): same reset as `selectConversation` plus clears the
   *  message list (going TO `null` doesn't trigger the load-on-change
   *  effect, so it must be cleared explicitly). */
  startNewConversation: () => void
  /** The active conversation was removed out from under this pane (e.g. its
   *  sidebar entry was deleted): same as `startNewConversation` but does NOT
   *  touch `sendError`/the pending focus block — matches the pre-extraction
   *  delete-branch exactly. */
  discardActiveConversation: () => void
  messages: TutorViewMessage[]
  messagesLoading: boolean
  draft: string
  setDraft: (value: string) => void
  sending: boolean
  sendError: string | null
  isBlocked: boolean
  limitResetsAt: string | null
  /** True while a focus block is still attached and unconsumed (i.e. no
   *  message has been sent in this conversation yet) — drives the
   *  block-specific empty-state copy. */
  hasPendingFocusBlock: boolean
  handleSend: () => Promise<void>
  handleComposerKeyDown: (event: React.KeyboardEvent<HTMLTextAreaElement>) => void
  handleFeedback: (
    messageId: string,
    currentValue: 'up' | 'down' | null,
    value: 'up' | 'down',
  ) => void
  handleRunAction: (action: TutorActionView) => void
  citationPopup: {
    citation: TutorCitationView
    status: TutorCitationDialogStatus
    block: TutorBlockContent | null
    errorMessage: string | null
  } | null
  handleOpenCitation: (citation: TutorCitationView) => void
  handleCloseCitationPopup: () => void
  handleOpenCitationInManual: () => void
  scrollEndRef: React.RefObject<HTMLDivElement | null>
}

export function useTutorConversation({
  manualVersionId,
  initialConversationId,
  initialMessages,
  focusBlock,
  resolveSectionTarget,
  onNavigateToSection,
  onLaunchQuiz,
  onConversationCreated,
  onTurnSettled,
}: UseTutorConversationArgs): UseTutorConversationResult {
  const [conversationId, setConversationId] = useState<string | null>(initialConversationId ?? null)
  const [messages, setMessages] = useState<TutorViewMessage[]>(() => initialMessages ?? [])
  const [messagesLoading, setMessagesLoading] = useState(false)

  const [draft, setDraft] = useState('')
  const [sending, setSending] = useState(false)
  const [sendError, setSendError] = useState<string | null>(null)

  const [limitResetsAt, setLimitResetsAt] = useState<string | null>(null)
  const [now, setNow] = useState(() => Date.now())

  // Citation popup (TASK-012 REVISIONE 2 Parte C) — null when closed. Set
  // directly by handleOpenCitation (a click handler), never derived in an
  // effect: the fetch it kicks off is a side effect of THAT click, not a
  // reactive consequence of some other state changing.
  const [citationPopup, setCitationPopup] = useState<{
    citation: TutorCitationView
    status: TutorCitationDialogStatus
    block: TutorBlockContent | null
    errorMessage: string | null
  } | null>(null)

  const streamAbortRef = useRef<AbortController | null>(null)
  const nextLocalIdRef = useRef(0)
  const scrollEndRef = useRef<HTMLDivElement | null>(null)
  /**
   * When handleSend creates a brand-new conversation and immediately selects
   * it, that selectedId change would otherwise trigger the message-loading
   * effect below, whose GET races the optimistic user/assistant bubbles
   * handleSend appends right after — the fetch (a conversation guaranteed to
   * have zero messages server-side at that instant) resolves and wipes them
   * out from under the in-flight stream update. Set right before that one
   * programmatic setConversationId call to skip that single redundant/racy
   * load.
   *
   * Also seeded `true` at construction when the caller passed BOTH an
   * `initialConversationId` and `initialMessages` (TASK-014 Step B2) — the
   * effect's very first run (triggered by that non-null initial
   * conversationId) would otherwise immediately re-fetch messages the caller
   * already handed us. `useRef`'s initializer only matters on the FIRST
   * render, exactly the "read once at construction" contract `initialMessages`
   * itself documents.
   */
  const suppressNextLoadRef = useRef(initialConversationId != null && initialMessages != null)

  /**
   * The focus block captured at CONSTRUCTION time — this hook instance's
   * whole lifetime corresponds to one "conversation slot" (a sidebar
   * selection, or one TutorDrawer instance keyed to remount per TASK block
   * click), attached to the very next conversation this instance creates.
   * Cleared as soon as it's consumed by handleSend, or if the caller instead
   * switches to a DIFFERENT conversation first (selectConversation /
   * startNewConversation) — in both of those cases the "chat about this
   * specific block" intent no longer applies to whatever conversation ends
   * up being used.
   */
  const pendingFocusBlockRef = useRef<TutorOverlayFocusBlock | null>(focusBlock ?? null)
  const [hasPendingFocusBlock, setHasPendingFocusBlock] = useState(focusBlock != null)

  // ── Load messages when the active conversation changes ──────────────────
  // Deliberately does NOT setMessages([]) for the `conversationId == null`
  // case: every caller that transitions conversationId to null
  // (startNewConversation, discardActiveConversation) already clears
  // `messages` itself, synchronously, in the SAME event handler — doing it
  // again here would just be a redundant setState-during-render-commit
  // (flagged by react-hooks/set-state-in-effect) for a value that's already
  // correct. The only other way conversationId is null is the initial
  // mount, where `messages` already defaults to `[]`.
  useEffect(() => {
    if (conversationId == null) return
    if (suppressNextLoadRef.current) {
      suppressNextLoadRef.current = false
      return
    }
    let cancelled = false
    setMessagesLoading(true)
    fetchTutorConversation(manualVersionId, conversationId)
      .then((detail) => {
        if (cancelled) return
        setMessages(toViewMessages(detail.messages))
      })
      .catch((err: unknown) => {
        if (!cancelled) setSendError(err instanceof Error ? err.message : String(err))
      })
      .finally(() => {
        if (!cancelled) setMessagesLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [manualVersionId, conversationId])

  // `isBlocked` is fully DERIVED from `limitResetsAt`/`now` — deliberately
  // never written back to state once expired (no `setLimitResetsAt(null)`
  // in an effect, which react-hooks/set-state-in-effect correctly flags:
  // clearing derived state synchronously in an effect just to re-derive the
  // same thing render already computes is redundant, not idiomatic React).
  // The banner's visibility and the composer's disabled state both key off
  // this one value; `limitResetsAt` itself is left as "last reported reset
  // time" and is harmlessly overwritten by the next `limit_reached` event.
  const isBlocked = limitResetsAt != null && now < new Date(limitResetsAt).getTime()

  // ── Budget banner countdown — tick `now` every 30s only while blocked;
  // the interval stops itself (via the cleanup) once isBlocked flips false.
  useEffect(() => {
    if (!isBlocked) return
    const id = setInterval(() => setNow(Date.now()), 30_000)
    return () => clearInterval(id)
  }, [isBlocked])

  // ── Abort any in-flight stream on unmount ───────────────────────────────
  useEffect(
    () => () => {
      streamAbortRef.current?.abort()
    },
    [],
  )

  // ── Auto-scroll to the newest content ───────────────────────────────────
  useEffect(() => {
    scrollEndRef.current?.scrollIntoView({ block: 'end' })
  }, [messages])

  const selectConversation = useCallback(
    (id: string) => {
      if (id === conversationId) return
      streamAbortRef.current?.abort()
      setSending(false)
      setSendError(null)
      setConversationId(id)
      pendingFocusBlockRef.current = null
      setHasPendingFocusBlock(false)
    },
    [conversationId],
  )

  const startNewConversation = useCallback(() => {
    streamAbortRef.current?.abort()
    setSending(false)
    setSendError(null)
    setConversationId(null)
    setMessages([])
    pendingFocusBlockRef.current = null
    setHasPendingFocusBlock(false)
  }, [])

  const discardActiveConversation = useCallback(() => {
    streamAbortRef.current?.abort()
    setSending(false)
    setConversationId(null)
    setMessages([])
  }, [])

  const handleSend = useCallback(async () => {
    const text = draft.trim()
    if (text.length === 0 || sending || isBlocked) return

    setDraft('')
    setSendError(null)

    let activeConversationId = conversationId
    if (activeConversationId == null) {
      try {
        const created = await createTutorConversation(
          manualVersionId,
          focusBlockPayload(pendingFocusBlockRef.current),
        )
        activeConversationId = created.id
        pendingFocusBlockRef.current = null
        setHasPendingFocusBlock(false)
        suppressNextLoadRef.current = true
        setConversationId(created.id)
        onConversationCreated?.(created)
      } catch (err) {
        setSendError(err instanceof Error ? err.message : String(err))
        setDraft(text)
        return
      }
    }
    const conversationIdForTurn = activeConversationId

    const userLocalId = `local-u-${nextLocalIdRef.current++}`
    const assistantLocalId = `local-a-${nextLocalIdRef.current++}`

    setMessages((prev) => [
      ...prev,
      {
        id: userLocalId,
        role: 'user',
        content: text,
        citations: [],
        action: null,
        pending: false,
        toolStep: null,
        feedback: null,
        persisted: false,
      },
      {
        id: assistantLocalId,
        role: 'assistant',
        content: '',
        citations: [],
        action: null,
        pending: true,
        toolStep: null,
        feedback: null,
        persisted: false,
      },
    ])

    setSending(true)
    const controller = new AbortController()
    streamAbortRef.current = controller

    let turn: TutorTurnState = EMPTY_TURN_STATE
    const applyToBubble = () => {
      setMessages((prev) =>
        prev.map((m) =>
          m.id === assistantLocalId
            ? {
                ...m,
                content: turn.error ?? turn.text,
                citations: turn.citations.map((c) => ({
                  section_id: c.section_id,
                  sub_para_id: c.sub_para_id,
                  block_id: c.block_id,
                  title: c.title,
                  quote: c.quote,
                })),
                action: viewActionFromEvent(turn.action),
                toolStep: turn.activeToolStep,
              }
            : m,
        ),
      )
    }

    try {
      await streamTutorMessage({
        manualVersionId,
        conversationId: conversationIdForTurn,
        message: text,
        signal: controller.signal,
        onEvent: (event) => {
          turn = applyTutorEvent(turn, event)
          applyToBubble()
        },
      })
    } catch (err) {
      const isAbort = err instanceof DOMException && err.name === 'AbortError'
      if (!isAbort) {
        turn = { ...turn, error: turn.error ?? (err instanceof Error ? err.message : String(err)) }
        applyToBubble()
      }
    } finally {
      setSending(false)
      streamAbortRef.current = null

      const succeeded = turn.error == null && turn.limitReachedAt == null
      if (succeeded) {
        // Refetch so this turn's assistant message gets its REAL persisted
        // id (needed for feedback — the `done` event carries no id) and
        // server-authoritative citations/tool_data, replacing the
        // optimistic local-id bubbles for this turn. On `error`/
        // `limit_reached` the assistant row was never saved (see
        // Tutor::ChatRunner), so refetching would just erase the error
        // bubble the student is looking at — skip it in those cases.
        try {
          const detail = await fetchTutorConversation(manualVersionId, conversationIdForTurn)
          setMessages(toViewMessages(detail.messages))
        } catch {
          // Best-effort — leave the optimistic bubbles settled (non-pending);
          // feedback stays unavailable on them until the next successful load.
          setMessages((prev) =>
            prev.map((m) => (m.id === assistantLocalId ? { ...m, pending: false } : m)),
          )
        }
      } else {
        setMessages((prev) =>
          prev.map((m) => (m.id === assistantLocalId ? { ...m, pending: false } : m)),
        )
      }

      if (turn.limitReachedAt != null) setLimitResetsAt(turn.limitReachedAt)
      onTurnSettled?.()
    }
  }, [
    draft,
    sending,
    isBlocked,
    conversationId,
    manualVersionId,
    onConversationCreated,
    onTurnSettled,
  ])

  // The actual deep-link (TASK-010's original onOpenCitation behavior) — now
  // only invoked from the popup's "Apri nel manuale" button, or directly as
  // a fallback for a citation with no block_id (nothing a popup could show).
  const navigateToCitation = useCallback(
    (citation: TutorCitationView) => {
      const fine = citation.block_id ?? citation.sub_para_id ?? null
      const target = resolveSectionTarget(citation.section_id, fine)
      if (target != null) onNavigateToSection(target.slug, target.anchor)
    },
    [resolveSectionTarget, onNavigateToSection],
  )

  // TASK-012 REVISIONE 2 Parte C: clicking a citation now opens the in-page
  // popup instead of navigating straight away. The fetch is kicked off HERE
  // (a click handler), not in a useEffect keyed off `citationPopup` — this
  // IS the side effect of the click, not a reactive derivation of state.
  const handleOpenCitation = useCallback(
    (citation: TutorCitationView) => {
      const blockId = citation.block_id
      if (blockId == null) {
        // Older citation predating block_id — nothing a popup could show;
        // fall back to the original direct deep-link.
        navigateToCitation(citation)
        return
      }

      setCitationPopup({ citation, status: 'loading', block: null, errorMessage: null })
      void fetchTutorBlockContent(manualVersionId, blockId)
        .then((block) => {
          setCitationPopup((prev) =>
            prev != null && prev.citation === citation ? { ...prev, status: 'ready', block } : prev,
          )
        })
        .catch((err: unknown) => {
          setCitationPopup((prev) =>
            prev != null && prev.citation === citation
              ? {
                  ...prev,
                  status: 'error',
                  errorMessage: err instanceof Error ? err.message : String(err),
                }
              : prev,
          )
        })
    },
    [manualVersionId, navigateToCitation],
  )

  const handleCloseCitationPopup = useCallback(() => setCitationPopup(null), [])

  const handleOpenCitationInManual = useCallback(() => {
    if (citationPopup == null) return
    navigateToCitation(citationPopup.citation)
    setCitationPopup(null)
  }, [citationPopup, navigateToCitation])

  const handleRunAction = useCallback(
    (action: TutorActionView) => {
      if (action.kind === 'launch_quiz') {
        onLaunchQuiz(action.quizSessionId)
      } else if (action.kind === 'open_section') {
        const target = resolveSectionTarget(action.sectionId, null)
        if (target != null) onNavigateToSection(target.slug, target.anchor)
      }
    },
    [resolveSectionTarget, onNavigateToSection, onLaunchQuiz],
  )

  // Toggle: clicking the already-active vote clears it; clicking the other
  // one switches. `currentValue` is read from the message at the call site
  // (message.feedback) rather than looked up here, so this stays a pure
  // function of its arguments — no stale-closure risk across rapid clicks.
  const handleFeedback = useCallback(
    (messageId: string, currentValue: 'up' | 'down' | null, value: 'up' | 'down') => {
      if (conversationId == null) return
      const conversationIdForFeedback = conversationId
      const nextValue = currentValue === value ? null : value

      setMessages((prev) =>
        prev.map((m) => (m.id === messageId ? { ...m, feedback: nextValue } : m)),
      )

      void setMessageFeedback(
        manualVersionId,
        conversationIdForFeedback,
        messageId,
        nextValue,
      ).catch(() => {
        // Roll back the optimistic flip on failure.
        setMessages((prev) =>
          prev.map((m) => (m.id === messageId ? { ...m, feedback: currentValue } : m)),
        )
      })
    },
    [manualVersionId, conversationId],
  )

  const handleComposerKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
      if (event.key === 'Enter' && !event.shiftKey) {
        event.preventDefault()
        void handleSend()
      }
    },
    [handleSend],
  )

  return {
    conversationId,
    setConversationId,
    selectConversation,
    startNewConversation,
    discardActiveConversation,
    messages,
    messagesLoading,
    draft,
    setDraft,
    sending,
    sendError,
    isBlocked,
    limitResetsAt,
    hasPendingFocusBlock,
    handleSend,
    handleComposerKeyDown,
    handleFeedback,
    handleRunAction,
    citationPopup,
    handleOpenCitation,
    handleCloseCitationPopup,
    handleOpenCitationInManual,
    scrollEndRef,
  }
}
